const BLOG_TOPICS_DRAWER_QUERY = "(max-width: 989px)";
const BLOG_TOPICS_BOTTOM_GAP = 24;
const BLOG_TOPICS_MIN_HEIGHT = 220;

class BlogTopics extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector(".blog-topics__panel");
    this.input = this.querySelector("[data-topics-search]");
    this.empty = this.querySelector(".blog-topics__empty");
    this.scroller = this.querySelector(".blog-topics__scroller");
    // "Покажи всички" is a reset, not a topic — it never filters out.
    this.items = Array.from(this.querySelectorAll(".blog-topics__item:not(.blog-topics__item--all)"));

    // The trigger lives in the article column, not inside this element.
    this.trigger = document.querySelector("[data-topics-open]");

    if (!this.panel) return;

    this.terms = this.items.map((item) => this.normalize(item.textContent));
    this.drawer = window.matchMedia(BLOG_TOPICS_DRAWER_QUERY);

    this.onSearch = this.onSearch.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onViewport = this.onViewport.bind(this);

    if (this.input) this.input.addEventListener("input", this.onSearch);
    if (this.trigger) this.trigger.addEventListener("click", () => this.open());

    this.querySelector("[data-topics-close]")?.addEventListener("click", () => this.close());
    this.querySelector("[data-topics-backdrop]")?.addEventListener("click", () => this.close());
    document.addEventListener("keydown", this.onKeydown);
    this.drawer.addEventListener("change", this.onBreakpoint);

    window.addEventListener("scroll", this.onViewport, { passive: true });
    window.addEventListener("resize", this.onViewport);
    if (!this.drawer.matches) this.fit();
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeydown);
    this.drawer?.removeEventListener("change", this.onBreakpoint);
    window.removeEventListener("scroll", this.onViewport);
    window.removeEventListener("resize", this.onViewport);
    this.releaseScroll();
  }

  normalize(value) {
    return value.trim().toLocaleLowerCase("bg");
  }

  get isOpen() {
    return this.classList.contains("is-open");
  }

  open() {
    this.classList.add("is-open");
    this.trigger?.setAttribute("aria-expanded", "true");
    if (this.drawer.matches) {
      // bodyScrollLock alone is a no-op in some browsers, so pin the body too.
      bodyScroll.lock(this.scroller);
      document.body.classList.add("blog-topics-open");
    }
    (this.input || this.panel).focus({ preventScroll: true });
  }

  close(refocus = true) {
    if (!this.isOpen) return;
    this.classList.remove("is-open");
    this.trigger?.setAttribute("aria-expanded", "false");
    this.releaseScroll();
    if (refocus) this.trigger?.focus({ preventScroll: true });
  }

  releaseScroll() {
    if (typeof bodyScroll !== "undefined" && this.scroller) bodyScroll.unlock(this.scroller);
    document.body.classList.remove("blog-topics-open");
  }

  onKeydown(event) {
    if (event.key === "Escape" && this.isOpen) this.close();
  }

  // Crossing the breakpoint in either direction resets the drawer, so a resize
  // never leaves the page scroll-locked or the drawer pre-opened.
  onBreakpoint() {
    this.close(false);
    this.onViewport();
  }

  // The rail sticks partway down the page, so a viewport-relative max-height in
  // CSS strands the last topics below the fold until you scroll. Measure the
  // rail's actual top instead and give it exactly the room that is left.
  onViewport() {
    if (this.drawer.matches) {
      this.panel.style.maxHeight = "";
      return;
    }
    // Cancel-and-reissue rather than a latch: a boolean guard stays stuck
    // forever if a frame never arrives (background tab, throttling).
    cancelAnimationFrame(this.fitFrame);
    this.fitFrame = requestAnimationFrame(() => this.fit());
  }

  fit() {
    const top = Math.max(this.getBoundingClientRect().top, 0);
    const room = window.innerHeight - top - BLOG_TOPICS_BOTTOM_GAP;
    this.panel.style.maxHeight = Math.max(room, BLOG_TOPICS_MIN_HEIGHT) + "px";
  }

  onSearch() {
    const query = this.normalize(this.input.value);
    let matches = 0;

    this.items.forEach((item, index) => {
      const hidden = query !== "" && !this.terms[index].includes(query);
      item.hidden = hidden;
      if (!hidden) matches += 1;
    });

    if (this.empty) this.empty.hidden = matches !== 0;
  }
}

customElements.define("blog-topics", BlogTopics);
